import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { HelpCircle, Plus, Trash2, Edit2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { category: "", question: "", answer: "", sort_order: 0 };

const AdminFaq = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // faq being edited, or null for create
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const submitRef = useRef(false);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/faqs`);
      setFaqs(res.data || []);
    } catch (e) {
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const f of faqs) {
      const cat = f.category || "Uncategorized";
      (map[cat] = map[cat] || []).push(f);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [faqs]);

  const categories = useMemo(
    () => Array.from(new Set(faqs.map((f) => f.category).filter(Boolean))).sort(),
    [faqs]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (faq) => {
    setEditing(faq);
    setForm({
      category: faq.category || "",
      question: faq.question || "",
      answer: faq.answer || "",
      sort_order: faq.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitRef.current) return;
    if (!form.category.trim() || !form.question.trim() || !form.answer.trim()) {
      toast.error("Category, question, and answer are all required");
      return;
    }
    submitRef.current = true;
    setSubmitting(true);
    const payload = {
      category: form.category.trim(),
      question: form.question.trim(),
      answer: form.answer.trim(),
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editing) {
        const res = await axios.put(`${API}/admin/faqs/${editing.id}`, payload);
        setFaqs((prev) => prev.map((f) => (f.id === editing.id ? res.data : f)));
        toast.success("FAQ updated");
      } else {
        const res = await axios.post(`${API}/admin/faqs`, payload);
        setFaqs((prev) => [...prev, res.data]);
        toast.success("FAQ added");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save FAQ");
    } finally {
      setSubmitting(false);
      submitRef.current = false;
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Delete this FAQ?\n\n"${faq.question}"`)) return;
    try {
      await axios.delete(`${API}/admin/faqs/${faq.id}`);
      setFaqs((prev) => prev.filter((f) => f.id !== faq.id));
      toast.success("FAQ deleted");
    } catch (e) {
      toast.error("Failed to delete FAQ");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <HelpCircle size={24} className="text-[#f4cc1e]" />
          <h1 className="text-2xl font-bold text-white font-['Chivo']">FAQ</h1>
          <span className="text-sm text-white/50">{faqs.length} total</span>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold uppercase tracking-wide rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Add FAQ
        </Button>
      </div>

      {faqs.length === 0 ? (
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardContent className="p-12 text-center text-white/50">
            <HelpCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p>No FAQs yet</p>
            <p className="text-sm mt-1">Add your first question and answer to get started.</p>
          </CardContent>
        </Card>
      ) : (
        grouped.map(([category, items]) => (
          <Card key={category} className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardHeader className="py-3 px-5 border-b border-[#1a3a6a]">
              <CardTitle className="font-['Chivo'] text-base text-[#f4cc1e]">{category}</CardTitle>
            </CardHeader>
            <CardContent className="px-5 py-2">
              {items
                .slice()
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map((faq) => (
                  <div
                    key={faq.id}
                    className="flex items-start justify-between gap-3 py-4 border-b border-[#1a3a6a] last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{faq.question}</p>
                      <p className="text-sm text-white/60 mt-1 whitespace-pre-wrap">{faq.answer}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEdit(faq)}
                        title="Edit"
                        className="text-white/60 hover:text-[#f4cc1e] hover:bg-[#0d2a5a] h-8 w-8"
                      >
                        <Edit2 size={15} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(faq)}
                        title="Delete"
                        className="text-white/40 hover:text-red-400 hover:bg-red-500/10 h-8 w-8"
                      >
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white">
              {editing ? "Edit FAQ" : "Add FAQ"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-white/80">Category</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Billing, Scheduling, Leads"
                list="faq-categories"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
              <datalist id="faq-categories">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Question</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="The question customers ask"
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Answer</Label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                placeholder="The answer shown to the customer"
                rows={5}
                className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/30"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/80">Sort order (within category)</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className="bg-[#0d2a5a] border-[#1a3a6a] text-white w-28"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-white hover:bg-[#1a3a6a] border border-[#1a3a6a]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : editing ? "Save" : "Add FAQ"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFaq;
